import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ROUTES } from '@/routes/routeConfig';
import { useAuth } from '@/contexts/AuthContext';
import {
  ArrowLeft,
  Cog,
  Edit,
  Home,
  Monitor,
  Smartphone,
  Tablet,
  Save,
  Undo,
  Redo,
  ZoomIn,
  ZoomOut,
  Maximize,
  Loader2,
} from 'lucide-react';
import { useBuilderUIStore } from '@/hooks/builderStore/useBuilderUIStore';
import { useBuilderHeader } from '@/contexts/BuilderHeaderContext';
import { Separator } from '@/components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

import { UserAccountMenu } from '@/components/auth/UserAccountMenu';

interface BuilderHeaderProps {
  setShowPreview?: (show: boolean) => void;
  builderState?: {
    state?: {
      zoom?: number;
      viewMode?: 'desktop' | 'tablet' | 'mobile';
      isSaving?: boolean;
      saveStatus?: 'idle' | 'saving' | 'success' | 'error';
      history?: {
        past: unknown[];
        future: unknown[];
      };
    };
    dispatch?: (action: { type: string; payload?: unknown }) => void;
  };
}

export default function BuilderHeader({
  setShowPreview,
  builderState,
}: BuilderHeaderProps = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { landingPageID, interfaceId } = useParams<{
    landingPageID: string;
    interfaceId: string;
  }>();
  const viewMode = useBuilderUIStore((state) => state.viewMode);
  const setViewMode = useBuilderUIStore((state) => state.setViewMode);
  const { hasUnsavedChanges, setHasUnsavedChanges } = useBuilderHeader();

  const [pendingNavigation, setPendingNavigation] = useState<string | null>(
    null,
  );
  const [pendingSaveNavigation, setPendingSaveNavigation] = useState<
    string | null
  >(null);
  const hasStartedSaveForNavigationRef = useRef(false);

  const state = builderState?.state;
  const dispatch = builderState?.dispatch;
  const isSaving = state?.isSaving ?? false;
  const saveStatus = state?.saveStatus ?? 'idle';

  const performLogout = useCallback(async () => {
    await logout();
    navigate(ROUTES.LOGIN);
  }, [logout, navigate]);

  const navigateToPendingTarget = useCallback(
    (target: string) => {
      if (target === 'back') {
        navigate(-1);
        return;
      }

      if (target === 'logout') {
        void performLogout();
        return;
      }

      navigate(target);
    },
    [navigate, performLogout],
  );

  useEffect(() => {
    if (!pendingSaveNavigation) {
      hasStartedSaveForNavigationRef.current = false;
      return;
    }

    if (isSaving || saveStatus === 'saving') {
      hasStartedSaveForNavigationRef.current = true;
      return;
    }

    if (!hasStartedSaveForNavigationRef.current) {
      return;
    }

    if (saveStatus === 'error') {
      setPendingSaveNavigation(null);
      hasStartedSaveForNavigationRef.current = false;
      return;
    }

    if (saveStatus !== 'success') {
      return;
    }

    const target = pendingSaveNavigation;
    setPendingSaveNavigation(null);
    hasStartedSaveForNavigationRef.current = false;
    setHasUnsavedChanges(false);
    navigateToPendingTarget(target);
  }, [
    pendingSaveNavigation,
    isSaving,
    saveStatus,
    navigateToPendingTarget,
    setHasUnsavedChanges,
  ]);

  const isEditorPage = location.pathname.includes(ROUTES.BUILDER_EDITOR);
  const isPublishPage = location.pathname.includes(ROUTES.BUILDER_PUBLISH);
  const shouldConfirmLeave = isEditorPage && hasUnsavedChanges;

  const handleHomeClick = () => {
    if (shouldConfirmLeave) {
      setPendingNavigation(ROUTES.DASHBOARD);
      return;
    }

    navigate(ROUTES.DASHBOARD);
  };

  const handleBackClick = () => {
    if (shouldConfirmLeave) {
      setPendingNavigation('back');
      return;
    }

    navigate(-1);
  };

  const handleBackToPreviewClick = () => {
    const previewPath = interfaceId
      ? `${ROUTES.BUILDER}/${landingPageID}/${interfaceId}/${ROUTES.BUILDER_TEMPLATE_PREVIEW}`
      : null;

    if (shouldConfirmLeave) {
      setPendingNavigation(previewPath ?? 'back');
      return;
    }

    if (previewPath) {
      navigate(previewPath, { replace: false });
      return;
    }

    navigate(-1);
  };

  const handleSettingsClick = () => {
    if (!landingPageID || !interfaceId) return;

    const settingsPath = `${ROUTES.BUILDER}/${landingPageID}/${interfaceId}/${ROUTES.BUILDER_SETTINGS}`;

    if (shouldConfirmLeave) {
      setPendingNavigation(settingsPath);
      return;
    }

    navigate(settingsPath);
  };

  const handleSaveAndNavigate = () => {
    if (!dispatch || !pendingNavigation) {
      setPendingNavigation(null);
      return;
    }

    const target = pendingNavigation;
    setPendingNavigation(null);
    setPendingSaveNavigation(target);
    dispatch({ type: 'SAVE_TEMPLATE' });
  };

  const handleDiscardAndNavigate = () => {
    const target = pendingNavigation;
    if (!target) return;

    setPendingNavigation(null);
    dispatch?.({ type: 'SET_UNSAVED_CHANGES', payload: false });
    dispatch?.({ type: 'RESET_HISTORY' });
    setHasUnsavedChanges(false);
    navigateToPendingTarget(target);
  };

  const handleCancelNavigation = () => {
    setPendingNavigation(null);
  };

  const getPageTitle = () => {
    if (location.pathname.includes(ROUTES.BUILDER_PUBLISH)) {
      return 'Publish Website';
    }
    if (location.pathname.includes(ROUTES.BUILDER_EDITOR)) {
      return 'Canvas Builder';
    }
    if (location.pathname.includes(ROUTES.BUILDER_SETTINGS)) {
      return 'Builder Settings';
    }
    return 'Template Preview'; // Default fallback
  };

  const handleEdit = () => {
    if (!landingPageID) {
      console.warn('[BuilderHeader] Missing landingPageID');
      return;
    }

    // Navigate to /builder/{moduleId}/{interfaceId}/editor
    // If interfaceId is available, include it in the path
    const editorPath = interfaceId
      ? `${ROUTES.BUILDER}/${landingPageID}/${interfaceId}/${ROUTES.BUILDER_EDITOR}`
      : `${ROUTES.BUILDER}/${landingPageID}/${ROUTES.BUILDER_EDITOR}`;
    navigate(editorPath, { replace: false });
  };

  const handleSave = () => {
    if (!dispatch) return;
    dispatch({ type: 'SAVE_TEMPLATE' });
  };

  const handlePublish = () => {
    console.log(
      '[handlePublish] landingPageID:',
      landingPageID,
      'interfaceId:',
      interfaceId,
    );
    if (!dispatch || !landingPageID || !interfaceId) return;
    const publishPath = `${ROUTES.BUILDER}/${landingPageID}/${interfaceId}/${ROUTES.BUILDER_PUBLISH}`;
    console.log('[handlePublish] navigating to:', publishPath);
    setPendingSaveNavigation(publishPath);
    dispatch({ type: 'SAVE_TEMPLATE' });
  };

  const handleLogout = async () => {
    if (shouldConfirmLeave) {
      setPendingNavigation('logout');
      return;
    }

    await performLogout();
  };

  const handleUndo = () => {
    if (!dispatch) return;
    dispatch({ type: 'UNDO' });
    toast.info('Change undone');
  };

  const handleRedo = () => {
    if (!dispatch) return;
    dispatch({ type: 'REDO' });
    toast.info('Change redone');
  };

  const handleZoomIn = () => {
    if (!dispatch || !state) return;
    const newZoom = Math.min(state.zoom + 0.05, 1);
    dispatch({ type: 'SET_ZOOM', payload: newZoom });
  };

  const handleZoomOut = () => {
    if (!dispatch || !state) return;
    const newZoom = Math.max(state.zoom - 0.05, 0.3);
    dispatch({ type: 'SET_ZOOM', payload: newZoom });
  };

  const handleFitToScreen = () => {
    if (!dispatch) return;
    dispatch({ type: 'RESET_ZOOM' });
  };

  const handleViewModeChange = (mode: 'desktop' | 'tablet' | 'mobile') => {
    if (!dispatch) return;
    dispatch({ type: 'SET_VIEW_MODE', payload: mode });
  };

  const canUndo = state?.history.past.length > 0;
  const canRedo = state?.history.future.length > 0;

  return (
    <header className='top-0 right-0 left-0 z-50 sticky flex justify-between items-center bg-white shadow-sm p-4 border-gray-200 border-b w-full'>
      <div className='flex items-center'>
        <Tooltip delayDuration={50}>
          <TooltipTrigger asChild>
            <Button
              variant='outline'
              size='icon'
              onClick={handleHomeClick}
              className='mr-4'
            >
              <Home size={18} />
            </Button>
          </TooltipTrigger>
          <TooltipContent side='right'>Back to Dashboard</TooltipContent>
        </Tooltip>
        <Separator orientation='vertical' className='h-[30px]' />
        {!location.pathname.includes(ROUTES.BUILDER_TEMPLATE_PREVIEW) && (
          <Tooltip delayDuration={50}>
            <TooltipTrigger asChild>
              <Button
                variant='outline'
                size='icon'
                onClick={handleBackClick}
                className='ml-4'
              >
                <ArrowLeft size={18} />
              </Button>
            </TooltipTrigger>
            <TooltipContent side='right'>Back to Previous Page</TooltipContent>
          </Tooltip>
        )}

        <h1 className='bg-clip-text bg-gradient-to-r from-builder-primary to-purple-500 ml-4 font-semibold text-transparent text-xl'>
          {getPageTitle()}
        </h1>
      </div>
      <div className='flex items-center gap-3'>
        {isEditorPage && builderState && !isPublishPage ? (
          <>
            <Tooltip delayDuration={50}>
              <TooltipTrigger asChild>
                <Button variant='outline' onClick={handleBackToPreviewClick}>
                  <ArrowLeft size={16} /> Back to Preview
                </Button>
              </TooltipTrigger>
              <TooltipContent side='bottom'>
                Back to Template Preview
              </TooltipContent>
            </Tooltip>

            <div className='flex items-center gap-1 mx-2 px-4 border-r border-l'>
              <Tooltip delayDuration={50}>
                <TooltipTrigger asChild>
                  <Button
                    variant='outline'
                    size='icon'
                    onClick={handleUndo}
                    disabled={!canUndo}
                    title='Undo'
                    className='text-gray-500'
                  >
                    <Undo size={16} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side='bottom'>Undo</TooltipContent>
              </Tooltip>

              <Tooltip delayDuration={50}>
                <TooltipTrigger asChild>
                  <Button
                    variant='outline'
                    size='icon'
                    onClick={handleRedo}
                    disabled={!canRedo}
                    title='Redo'
                    className='text-gray-500'
                  >
                    <Redo size={16} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side='bottom'>Redo</TooltipContent>
              </Tooltip>
            </div>

            <div className='flex items-center gap-1 mx-2 px-4 border-r'>
              <Tooltip delayDuration={50}>
                <TooltipTrigger asChild>
                  <Button
                    variant='outline'
                    size='icon'
                    onClick={handleZoomOut}
                    title='Zoom Out'
                    className='text-gray-500'
                  >
                    <ZoomOut size={16} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side='bottom'>Zoom Out</TooltipContent>
              </Tooltip>

              <span className='min-w-[40px] text-gray-500 text-sm text-center'>
                {state ? Math.round(state.zoom * 100) : 100}%
              </span>

              <Tooltip delayDuration={50}>
                <TooltipTrigger asChild>
                  <Button
                    variant='outline'
                    size='icon'
                    onClick={handleZoomIn}
                    title='Zoom In'
                    className='text-gray-500'
                  >
                    <ZoomIn size={16} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side='bottom'>Zoom In</TooltipContent>
              </Tooltip>

              <Tooltip delayDuration={50}>
                <TooltipTrigger asChild>
                  <Button
                    variant='outline'
                    size='icon'
                    onClick={handleFitToScreen}
                    title='Fit to Screen'
                    className='text-gray-500'
                  >
                    <Maximize size={16} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side='bottom'>Fit to Screen</TooltipContent>
              </Tooltip>

              <BuilderSettingsButton onClick={handleSettingsClick} />
            </div>

            <div className='flex gap-1 bg-gray-100 p-1 rounded-md'>
              <Tooltip delayDuration={50}>
                <TooltipTrigger asChild>
                  <Button
                    variant={
                      (state?.viewMode || 'desktop') === 'desktop'
                        ? 'default'
                        : 'ghost'
                    }
                    size='icon'
                    onClick={() => handleViewModeChange('desktop')}
                    title='Desktop view'
                    className={
                      (state?.viewMode || 'desktop') === 'desktop'
                        ? 'bg-builder-primary text-white'
                        : 'text-gray-500'
                    }
                  >
                    <Monitor size={16} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side='bottom'>Desktop view</TooltipContent>
              </Tooltip>

              <Tooltip delayDuration={50}>
                <TooltipTrigger asChild>
                  <Button
                    variant={
                      (state?.viewMode || 'desktop') === 'tablet'
                        ? 'default'
                        : 'ghost'
                    }
                    size='icon'
                    onClick={() => handleViewModeChange('tablet')}
                    title='Tablet view'
                    className={
                      (state?.viewMode || 'desktop') === 'tablet'
                        ? 'bg-builder-primary text-white'
                        : 'text-gray-500'
                    }
                  >
                    <Tablet size={16} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side='bottom'>Tablet view</TooltipContent>
              </Tooltip>

              <Tooltip delayDuration={50}>
                <TooltipTrigger asChild>
                  <Button
                    variant={
                      (state?.viewMode || 'desktop') === 'mobile'
                        ? 'default'
                        : 'ghost'
                    }
                    size='icon'
                    onClick={() => handleViewModeChange('mobile')}
                    title='Mobile view'
                    className={
                      (state?.viewMode || 'desktop') === 'mobile'
                        ? 'bg-builder-primary text-white'
                        : 'text-gray-500'
                    }
                  >
                    <Smartphone size={16} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side='bottom'>Mobile view</TooltipContent>
              </Tooltip>
            </div>

            <Button
              onClick={handleSave}
              variant='outline'
              disabled={isSaving}
              className='hover:bg-builder-primary border-builder-primary text-builder-primary hover:text-white'
            >
              {isSaving ? (
                <Loader2 size={16} className='animate-spin' />
              ) : (
                <Save size={16} className='' />
              )}
              {isSaving ? 'Saving...' : 'Save'}
            </Button>

            <Button
              onClick={handlePublish}
              disabled={isSaving}
              className='hover:bg-builder-primary-hover bg-gradient-to-r from-builder-primary to-purple-500 text-white'
            >
              Publish
            </Button>

            {setShowPreview && (
              <Button
                variant='default'
                className='bg-green-600 hover:bg-green-700 px-4 py-2 font-semibold text-white'
                onClick={() => setShowPreview(true)}
                title='Preview Full Page'
              >
                Preview
              </Button>
            )}

            <UserAccountMenu user={user} onLogout={handleLogout} />
          </>
        ) : (
          <>
            {!isPublishPage && (
              <>
                <BuilderSettingsButton onClick={handleSettingsClick} />
                <Separator orientation='vertical' className='h-[30px]' />
                <div className='flex gap-1 bg-purple-100 p-1 rounded-md'>
                  <Button
                    variant={viewMode === 'desktop' ? 'default' : 'ghost'}
                    size='sm'
                    onClick={() => setViewMode('desktop')}
                    className={
                      viewMode === 'desktop'
                        ? 'bg-builder-primary text-white hover:bg-builder-primary-hover'
                        : 'hover:bg-purple-200'
                    }
                  >
                    <Monitor size={16} className='mr-2' />
                    Desktop
                  </Button>
                  <Button
                    variant={viewMode === 'tablet' ? 'default' : 'ghost'}
                    size='sm'
                    onClick={() => setViewMode('tablet')}
                    className={
                      viewMode === 'tablet'
                        ? 'bg-builder-primary text-white hover:bg-builder-primary-hover'
                        : ''
                    }
                  >
                    <Tablet size={16} className='mr-2' />
                    Tablet
                  </Button>
                  <Button
                    variant={viewMode === 'mobile' ? 'default' : 'ghost'}
                    size='sm'
                    onClick={() => setViewMode('mobile')}
                    className={
                      viewMode === 'mobile'
                        ? 'bg-builder-primary text-white hover:bg-builder-primary-hover'
                        : ''
                    }
                  >
                    <Smartphone size={16} className='mr-2' />
                    Mobile
                  </Button>
                </div>
                <Separator orientation='vertical' className='h-[30px]' />
              </>
            )}
            <Button
              onClick={handleEdit}
              className='bg-gradient-to-r from-builder-primary to-purple-500 hover:opacity-80 text-white transition-opacity duration-200'
            >
              <Edit size={18} className='mr-2' />
              Edit Template
            </Button>

            <UserAccountMenu user={user} onLogout={handleLogout} />
          </>
        )}
      </div>

      {/* Unsaved changes dialog */}
      <AlertDialog
        open={!!pendingNavigation}
        onOpenChange={(open) => {
          if (!open) {
            handleCancelNavigation();
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Do you want to save them before leaving?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelNavigation}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDiscardAndNavigate}>
              Discard
            </AlertDialogAction>
            <AlertDialogAction onClick={handleSaveAndNavigate}>
              Save
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={isSaving}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className='flex items-center gap-2'>
              <Loader2 className='w-4 h-4 animate-spin' />
              Saving your website
            </AlertDialogTitle>
            <AlertDialogDescription>
              Please wait while we save your latest design changes.
            </AlertDialogDescription>
          </AlertDialogHeader>
        </AlertDialogContent>
      </AlertDialog>
    </header>
  );
}

const BuilderSettingsButton = ({ onClick }: { onClick: () => void }) => {
  const { landingPageID, interfaceId } = useParams<{
    landingPageID: string;
    interfaceId?: string;
  }>();

  // Only show settings button when interfaceId is available
  if (!landingPageID || !interfaceId) {
    return null;
  }

  return (
    <Tooltip delayDuration={50}>
      <TooltipTrigger asChild>
        <Button
          variant='outline'
          className='group'
          size='icon'
          onClick={onClick}
        >
          <Cog className='text-gray-600 group-hover:rotate-90 transition-transform duration-600' />
        </Button>
      </TooltipTrigger>
      <TooltipContent side='bottom'>
        <p>Builder Settings</p>
      </TooltipContent>
    </Tooltip>
  );
};
